<template>
  <DetailSplitView :ready="licenseReady" :show-panel="Boolean(activePanel)">
    <template #top>
      <slot name="navigation">
        <IconButton
          icon="x-lg"
          title="Back to list"
          class="flex-shrink-0"
          @click="close"
        />
      </slot>

      <template v-if="licenseReady">
        <div class="d-flex flex-row align-items-center justify-content-start gap-0 flex-grow-1">
          <IconButton
            v-if="license.canRenew && isAuthenticated"
            icon="arrow-clockwise"
            @click="toggleExpandMode('renew')"
            :class="{ active: expandMode === 'renew' }"
          />
          <IconButton
            v-if="showDeleteButton"
            icon="trash"
            title="Delete"
            @click="toggleExpandMode('delete')"
            :class="{ active: expandMode === 'delete' }"
          />

          <div class="ms-auto d-flex flex-row align-items-center">
            <div class="d-flex flex-row align-items-center tab-actions me-1">
              <IconButton
                v-if="isAuthenticated"
                icon="link-45deg"
                :title="listingTabTitle"
                :class="['panel-tab', { 'panel-tab--active': activePanel === 'listings' }]"
                @click="togglePanel('listings')"
              />
              <IconButton
                v-if="canManageLetters"
                :icon="lettersCount > 0 ? 'envelope-fill' : 'envelope'"
                :title="lettersTabTitle"
                :class="['panel-tab', { 'panel-tab--active': activePanel === 'letters' }]"
                @click="togglePanel('letters')"
              />
              <IconButton
                v-if="canManageComments"
                icon="chat"
                :title="commentsTabTitle"
                :class="['panel-tab', { 'panel-tab--active': activePanel === 'comments' }]"
                @click="togglePanel('comments')"
              />
            </div>

            <div v-if="canViewApplicant || Boolean(license.latlng)" class="dropdown">
              <IconButton
                button
                icon="three-dots"
                class="flex-shrink-0 dropdown-toggle no-caret"
                data-bs-toggle="dropdown"
              />
              <ul class="dropdown-menu">
                <li v-if="canViewApplicant">
                  <IconButton
                    icon="printer"
                    text="Print application"
                    @click="printApplication"
                    small align="start" class="dropdown-item"
                  />
                </li>
                <li v-if="Boolean(license.latlng)">
                  <IconButton
                    icon="geo-alt"
                    text="Re-center map"
                    @click="showLocation"
                    small align="start" class="dropdown-item"
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template #after-top>
      <BCollapse :show="expandMode === 'delete'">
        <div class="confirm-strip">
          <label class="confirm-title">
            Are you sure you want to <template v-if="license.isDeleted"><i class="text-danger">permanently </i></template>delete this
            license?
          </label>
          <div class="d-flex flex-row">
            <div class="w-50 pe-1">
              <a class="btn btn-outline-danger w-100" @click="commitDelete">Yes, delete it</a>
            </div>
            <div class="w-50 ps-1">
              <a class="btn btn-outline-dark w-100" @click="expandMode = null">No, keep it</a>
            </div>
          </div>
        </div>
      </BCollapse>

      <BCollapse :show="expandMode === 'renew'">
        <div class="confirm-strip">
          <label class="confirm-title">
            Are you sure you want to renew this license?
          </label>
          <div class="d-flex flex-row">
            <div class="w-50 pe-1">
              <a class="btn btn-outline-danger w-100" @click="commitRenew">Yes, renew it</a>
            </div>
            <div class="w-50 ps-1">
              <a class="btn btn-outline-dark w-100" @click="expandMode = null">No</a>
            </div>
          </div>
        </div>
      </BCollapse>
    </template>

    <template #left>
      <div class="license-overview d-flex flex-column flex-grow-1 overflow-auto" ref="overviewScroller">
        <div class="license-content">

          <!-- Image -->
          <expandable-image v-if="license.imageUrl" :src="license.imageUrl" />

          <!-- Property -->
          <PropertySection :license="license" :editable="canViewApplicant" />

          <!-- License number -->
          <LicenseNumberSection :license="license" :editable="canViewApplicant" />

          <!-- License status / dates -->
          <StatusSection :license="license" :editable="canViewApplicant" />

          <!-- Applicant -->
          <ApplicantSection v-if="canViewApplicant" :license="license" editable />

          <section v-if="canManageLicenseEmails" class="license-section">
            <LicenseStatusEmailDeliverySection
              :license="license"
              @preview="openStatusEmailPreview"
            />
          </section>

          <section v-if="hasAdditionalDetails" class="license-section">
            <div class="application-answer-actions">
              <a
                href="#"
                class="section-action flex-grow-1"
                title="View full registration form"
                aria-label="View full registration form"
                @click.prevent="showAdditionalDetailsModal = true"
              >
                <div>View full registration form</div>
                <i class="bi-box-arrow-up-right" />
              </a>
              <button
                v-if="canViewApplicant"
                type="button"
                class="btn btn-sm btn-outline-secondary text-nowrap"
                @click="showApplicationAnswersModal = true"
              >
                <i class="bi-pencil me-1" />Edit answers
              </button>
            </div>
          </section>
        </div>
      </div>
    </template>

    <template #right>
      <div v-if="activePanel === 'comments' && canManageComments" class="w-100 h-100 d-flex flex-column overflow-hidden">
        <ActivityPanel :key="`license-activity-${activityRefreshKey}`" :model="license" class="h-100" />
      </div>

      <div v-else-if="activePanel === 'listings' && isAuthenticated" class="license-content">
        <detail-section :title="formatCount(listings.length, 'Listing') + ' verified at this property'" flush>
          <div v-if="loading" class="text-muted small px-3 pb-3">
            Loading listings...
          </div>
          <div v-else-if="listings.length === 0" class="text-muted small px-3 pb-3">
            This license hasn't been matched to any listings yet.
          </div>
          <div v-else class="list-group list-group-flush license-listings">
            <ListingNavigationRow
              v-for="listing in listings"
              :key="listing.id"
              :listing="listing"
              class="px-3"
              hide-address
            />
          </div>
        </detail-section>
      </div>

      <div v-else-if="activePanel === 'letters' && canManageLetters" class="license-content">
        <CorrespondencesView :property="lettersProperty" :license="license" />
      </div>
    </template>

    <template #fallback>
      <div class="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4">
        <LoadingMessage v-if="loadingLicense" message="Loading license" />
        <div v-else-if="licenseLoadError" class="text-danger text-center small">
          Could not load license details.
        </div>
        <div v-else class="text-muted text-center small">
          License not found.
        </div>
      </div>
    </template>
  </DetailSplitView>

  <ModalView
    v-if="licenseReady"
    v-model="showAdditionalDetailsModal"
    title="Additional Details"
    size="lg"
    scrollable
    hide-footer
  >
    <SurveyComponent v-if="survey" :model="survey" />
    <div v-else class="text-muted">No additional details were submitted.</div>
  </ModalView>

  <LicenseApplicationAnswersModal
    v-if="licenseReady && canViewApplicant"
    v-model="showApplicationAnswersModal"
    :license="license"
    @saved="onApplicationAnswersSaved"
  />

  <LicenseStatusEmailPreviewModal
    v-if="licenseReady"
    v-model="showStatusEmailPreview"
    :license="license"
    :rule="statusEmailPreviewRule"
    :test-recipient-email="currentUser?.email || null"
  />

  <LicenseStatusEmailConfirmationModal ref="renewalEmailConfirmation" />

  <PrintableLicenseApplication
    v-if="licenseReady && canViewApplicant"
    ref="printableApplication"
    :license="license"
  />
</template>

<script>
import IconButton from "@/components/common/IconButton.vue";
import {License} from "@/models/License";
import {Property} from "@/models/Property";
import {Session} from "@/models/User";
import {LicenseApplicationForm} from "@/models/LicenseApplicationForm";
import BCollapse from "@/components/common/BCollapse.vue";
import {SurveyComponent} from "survey-vue3-ui";
import SurveyModel from "@/models/SurveyModel";
import {Listing} from "@/models/Listing";
import ActivityPanel from "@/components/common/ActivityPanel.vue";
import ModalView from "@/components/common/ModalView.vue";
import CorrespondencesView from "@/components/common/CorrespondencesView.vue";
import LoadingMessage from "@/components/common/LoadingMessage.vue";
import DetailSection from "@/components/common/DetailSection.vue";
import PropertySection from "@/components/licenses/PropertySection.vue";
import StatusSection from "@/components/licenses/StatusSection.vue";
import ApplicantSection from "@/components/licenses/ApplicantSection.vue";
import DetailSplitView from "@/components/common/DetailSplitView.vue";
import ListingNavigationRow from "@/components/listing/ListingNavigationRow.vue";
import ExpandableImage from "@/components/common/ExpandableImage.vue";
import LicenseNumberSection from "@/components/licenses/LicenseNumberSection.vue";
import LicenseStatusEmailPreviewModal from '@/components/licenses/LicenseStatusEmailPreviewModal.vue';
import LicenseStatusEmailDeliverySection from '@/components/licenses/LicenseStatusEmailDeliverySection.vue';
import LicenseApplicationAnswersModal from '@/components/licenses/LicenseApplicationAnswersModal.vue';
import PrintableLicenseApplication from '@/components/licenses/PrintableLicenseApplication.vue';
import printElement from '@/printElement';
import LicenseStatusEmailConfirmationModal from '@/components/licenses/LicenseStatusEmailConfirmationModal.vue';
import { getLicenseStatusEmailRulesRequiringConfirmation } from '@strenforcement/domain/licenseStatusEmail';
import { showLicenseStatusEmailNotificationToasts } from '@/licenseStatusEmailNotification';

export default {
  components: {
    LicenseStatusEmailConfirmationModal,
    LicenseApplicationAnswersModal,
    PrintableLicenseApplication,
    LicenseStatusEmailDeliverySection,
    LicenseStatusEmailPreviewModal,
    LicenseNumberSection,
    ExpandableImage,
    DetailSplitView,
    ApplicantSection,
    StatusSection,
    PropertySection,
    DetailSection,
    LoadingMessage,
    CorrespondencesView,
    ModalView,
    SurveyComponent,
    ListingNavigationRow,
    ActivityPanel,
    BCollapse,
    IconButton,
  },
  emits: ['close', 'showLocation', 'update:panel'],
  props: {
    detailCity: Object,
    licenseId: String,
    panel: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      license: null,
      loadingLicense: false,
      licenseLoadError: null,
      loadToken: 0,
      listings: [],
      lettersProperty: null,
      expandMode: null,
      loading: false,
      survey: null,
      showAdditionalDetailsModal: false,
      showApplicationAnswersModal: false,
      showStatusEmailPreview: false,
      statusEmailPreviewRule: null,
      hasAdditionalDetails: false,
      showSnapshotImage: false,
      activityRefreshKey: 0
    };
  },
  watch: {
    canManageComments(value) {
      if (!value && this.activePanel === 'comments') {
        this.ensureValidPanel();
      }
    },
    isAuthenticated(value) {
      if (!value && (this.activePanel === 'listings' || this.activePanel === 'letters')) {
        this.ensureValidPanel();
      }
    },
    canManageLetters(value) {
      if (!value && this.activePanel === 'letters') {
        this.ensureValidPanel();
      }
    },
    panel(value) {
      if (value === 'overview') {
        this.ensureValidPanel({openDefaultIfMissing: true});
        return;
      }
      this.ensureValidPanel();
    },
    loadKey: {
      immediate: true,
      handler() {
        this.loadLicense();
      }
    }
  },
  methods: {
    async loadLicense() {
      const token = ++this.loadToken;
      this.loadingLicense = true;
      this.licenseLoadError = null;
      this.listings = [];
      this.lettersProperty = null;

      if (!this.detailCity || !this.licenseId) {
        this.license = null;
        this.loadingLicense = false;
        return;
      }
      this.license = null;

      try {
        const license = await License.one(this.detailCity, this.licenseId);
        if (token !== this.loadToken) {
          return;
        }
        this.license = license;
        await this.syncLoadedLicense(token);
      } catch (error) {
        if (token !== this.loadToken) {
          return;
        }
        console.error(`Could not load license ${this.licenseId}`, error);
        this.license = null;
        this.licenseLoadError = error;
      } finally {
        if (token === this.loadToken) {
          this.loadingLicense = false;
        }
      }
    },
    async syncLoadedLicense(token = this.loadToken) {
      if (token !== this.loadToken) {
        return;
      }
      this.$refs.overviewScroller?.scrollTo({top: 0, behavior: 'instant'});
      this.showAdditionalDetailsModal = false;
      this.showApplicationAnswersModal = false;
      this.showStatusEmailPreview = false;
      this.statusEmailPreviewRule = null;
      this.showSnapshotImage = false;
      this.expandMode = null;

      const form = this.license?.city ? new LicenseApplicationForm(this.license.city) : null;
      this.hasAdditionalDetails = Boolean(form?.published && form.supportsCustomFields);
      this.survey = this.hasAdditionalDetails ? SurveyModel.createFromLicense(this.license) : null;

      this.listings = [];
      this.lettersProperty = null;
      if (this.license?.propertyId) {
        try {
          const property = await Property.one(this.city, this.license.propertyId);
          if (token !== this.loadToken) {
            return;
          }
          this.lettersProperty = property;
        } catch (error) {
          console.error('Could not load property for license letters', error);
        }
      }
      if (this.isAuthenticated && this.license?.propertyId) {
        try {
          this.loading = true;
          const response = await Listing.query(this.city, {
            constraints: [['verifiedPropertyId', '==', this.license.propertyId]]
          });
          if (token !== this.loadToken) {
            return;
          }
          this.listings = response.results;
        } finally {
          this.loading = false;
        }
      }
      this.ensureValidPanel({openDefaultIfMissing: true});
    },
    isPanelAvailable(panel) {
      if (panel === 'comments') return this.canManageComments;
      if (panel === 'listings') return this.isAuthenticated;
      if (panel === 'letters') return this.canManageLetters;
      return false;
    },
    togglePanel(panel) {
      if (!this.isPanelAvailable(panel)) {
        return;
      }
      this.activePanel = this.activePanel === panel ? null : panel;
    },
    ensureValidPanel(options = {}) {
      const {openDefaultIfMissing = false} = options;
      if (this.activePanel && !this.isPanelAvailable(this.activePanel)) {
        this.activePanel = this.defaultPanel;
        return;
      }
      if (openDefaultIfMissing && !this.activePanel && this.defaultPanel) {
        this.activePanel = this.defaultPanel;
      }
    },
    showLocation() {
      if (this.license?.latlng) {
        this.$emit('showLocation', this.license.latlng);
      }
    },
    close(message) {
      this.$emit('close', message || false);
    },
    toggleExpandMode(expandMode) {
      this.expandMode = this.expandMode === expandMode ? null : expandMode;
    },
    commitDelete() {
      if (this.license) {
        this.license.delete(this.license.isDeleted)
        .then(() => this.expandMode = null);
      }
    },
    async commitRenew() {
      if (!this.license) return;
      try {
        const rules = getLicenseStatusEmailRulesRequiringConfirmation({
          rules: this.license.city.licenseStatusEmailRules,
          nextStatus: this.license.data.status,
          statusConfig: this.license.city.licenseStatuses,
          renewal: true
        });
        let emailNotificationDecisions;
        if (rules.length && this.license.contact?.email) {
          emailNotificationDecisions = await this.$refs.renewalEmailConfirmation.guard({
            recipientEmail: this.license.contact.email, rules, renewal: true
          });
          if (!emailNotificationDecisions) return;
        }
        await this.license.renew({ emailNotificationDecisions });
        this.expandMode = null;
        showLicenseStatusEmailNotificationToasts(this, this.license.emailNotifications);
      } catch (error) {
        this.$showToast(error.message || 'Could not renew the license.', { type: 'error' });
      }
    },
    printApplication() {
      printElement(this.$refs.printableApplication);
    },
    openStatusEmailPreview(rule) {
      this.statusEmailPreviewRule = rule;
      this.showStatusEmailPreview = true;
    },
    onApplicationAnswersSaved() {
      this.survey = SurveyModel.createFromLicense(this.license);
      this.activityRefreshKey += 1;
      this.$showToast('Application answers updated.', { type: 'success' });
    },
  },
  computed: {
    loadKey() {
      return `${this.detailCity?.id || ''}/${this.licenseId || ''}`;
    },
    licenseReady() {
      return Boolean(this.license?.hasData);
    },
    activePanel: {
      get() {
        if (!this.panel || this.panel === 'overview') {
          return null;
        }
        return this.panel;
      },
      set(activePanel) {
        this.$emit('update:panel', activePanel || null);
      }
    },
    currentUser() {
      return Session.currentUser.value;
    },
    isAuthenticated() {
      return Boolean(this.currentUser);
    },
    city() {
      return this.license?.city || this.detailCity || null;
    },
    showDeleteButton() {
      return false;
      // const user = Session.currentUser.value;
      // return user && user.role === 'admin';
    },
    canManageComments() {
      const user = this.currentUser;
      if (!user || !this.city) return false;
      return user.isAdmin || (user.cities || []).includes(this.city.id);
    },
    canViewApplicant() {
      return this.canManageComments;
    },
    canManageLicenseEmails() {
      return this.canViewApplicant && this.licenseReady;
    },
    canManageLetters() {
      return this.isAuthenticated && !!this.lettersProperty;
    },
    defaultPanel() {
      if (this.canManageComments) {
        return 'comments';
      }
      if (this.isAuthenticated) {
        return 'listings';
      }
      if (this.canManageLetters) {
        return 'letters';
      }
      return null;
    },
    lettersCount() {
      return this.lettersProperty?.correspondences?.length || 0;
    },
    lettersTabTitle() {
      const count = this.lettersCount;
      return `${count || 'No'} Letter${count === 1 ? '' : 's'}`;
    },
    listingTabTitle() {
      const count = this.listings.length;
      return `${count || 'No'} Listing${count === 1 ? '' : 's'}`;
    },
    commentsTabTitle() {
      const count = this.license?.commentCount || 0;
      return `${count || 'No'} Comment${count === 1 ? '' : 's'}`;
    }
  }
}
</script>

<style scoped>
.confirm-strip {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color);
  background: #f9fafb;
}

.confirm-title {
  display: block;
  font-weight: 500;
  margin-bottom: 0.625rem;
}

.license-content {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.license-section {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.license-content .license-section:last-child {
  border-top: 0;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.75;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.section-caption {
  font-size: 0.78rem;
  color: var(--bs-secondary-color);
}

.section-action {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: var(--bs-secondary-color) !important;
  text-decoration: none;
  line-height: 1.5;
}
.section-action:hover {
  text-decoration: underline;
  color: var(--bs-body-color) !important;
  opacity: 1;
}

.application-answer-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.detail-key {
  font-size: 0.78rem;
  color: var(--bs-secondary-color);
}

.detail-value {
  font-size: 0.85rem;
  text-align: right;
}

.detail-block {
  font-size: 0.82rem;
}

.contact-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contact-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px;
  align-items: center;
  column-gap: 0.5rem;
}

.contact-value {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.82rem;
  color: #55616f;
}

.contact-value i {
  font-size: 0.75rem;
}

.contact-action {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #dfe5ec;
  background: #f7f9fc;
  color: #55616f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

.contact-action:hover {
  background: #eef3f8;
  border-color: #ccd6e2;
  color: #334155;
}

.license-listings :deep(.list-group-item) {
  border-left: 0 !important;
  border-right: 0 !important;
  padding-left: 0;
  padding-right: 0;
}
</style>
